import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppContext } from '../../context/AppContext';
import { useCart } from '../../context/CartContext';
import ProductCard from './ProductCard';
import ProductOptionsModal from './ProductOptionsModal';
import OfflineService from '../../services/OfflineService';
import logo from '../../assets/logo-danes.png';
import logoHorizontal from '../../assets/logo-danes-horizontal.png';
import BrandLogo from '../common/BrandLogo';
import StepIndicator from '../layout/StepIndicator';

const np = (p) => (Array.isArray(p) ? (p[0] ?? null) : (p ?? null));

// Navegación jerárquica (pos.category): raíces a la izquierda; al elegir una,
// sus subcategorías aparecen al centro; los productos solo en la categoría HOJA.
const ProductListWithSidebar = () => {
    const { posCategories, sessionData } = useAppContext();
    const { addItem } = useCart();
    const navigate = useNavigate();

    const [activeCategoryId, setActiveCategoryId] = useState(null);
    const [selectedProduct, setSelectedProduct] = useState(null);
    const [isOffline, setIsOffline] = useState(!navigator.onLine);

    useEffect(() => {
        if (!sessionData || !sessionData.authenticated) navigate('/login', { replace: true });
    }, [sessionData, navigate]);

    useEffect(() => {
        const on = () => setIsOffline(false);
        const off = () => setIsOffline(true);
        window.addEventListener('online', on);
        window.addEventListener('offline', off);
        return () => { window.removeEventListener('online', on); window.removeEventListener('offline', off); };
    }, []);

    const cats = useMemo(() => posCategories || [], [posCategories]);
    const byId = (id) => cats.find(c => c.id === id);
    const childrenOf = (id) => cats.filter(c => np(c.parent_id) === id);
    const roots = useMemo(() => {
        const base = cats.filter(c => np(c.parent_id) === null);
        // HARDCODE temporal (frontend): bajar "DESAYUNO" para que quede justo después
        // de "COMPLETO NORMALES". No cambia el orden real en el backend.
        const norm = s => (s || '').toString().trim().toUpperCase();
        const desIdx = base.findIndex(c => norm(c.name) === 'DESAYUNO');
        const compIdx = base.findIndex(c => norm(c.name) === 'COMPLETO NORMALES');
        if (desIdx !== -1 && compIdx !== -1) {
            const [des] = base.splice(desIdx, 1);
            const target = base.findIndex(c => norm(c.name) === 'COMPLETO NORMALES');
            base.splice(target + 1, 0, des);
        }
        return base;
    }, [cats]);
    const rootOf = (id) => { let n = byId(id); while (n && np(n.parent_id) != null) n = byId(np(n.parent_id)); return n; };

    // Arrancar en la primera raíz
    useEffect(() => {
        if (activeCategoryId == null && roots.length > 0) setActiveCategoryId(roots[0].id);
    }, [roots, activeCategoryId]);

    const products = useMemo(() => {
        if (sessionData && Array.isArray(sessionData.products)) return sessionData.products.filter(p => p.is_totem);
        if (isOffline) return OfflineService.getProducts().filter(p => p.is_totem);
        return [];
    }, [sessionData, isOffline]);

    const activeCat = activeCategoryId != null ? byId(activeCategoryId) : null;
    const subcats = activeCategoryId != null ? childrenOf(activeCategoryId) : [];
    const isLeaf = activeCategoryId != null && subcats.length === 0;
    const activeRoot = activeCategoryId != null ? rootOf(activeCategoryId) : null;

    const leafProducts = useMemo(
        () => (isLeaf ? products.filter(p => p.pos_categ_id === activeCategoryId) : []),
        [isLeaf, products, activeCategoryId]
    );
    const goUp = () => { if (activeCat) setActiveCategoryId(np(activeCat.parent_id)); };

    const handleAddToCart = (product) => {
        if (product.custom_attributes && product.custom_attributes.length > 0) setSelectedProduct(product);
        else addItem(product);
    };
    const handleModalSubmit = (selectedQuantities, totalPrice, attributesDescription, attribute_lines, nested_attributes) => {
        addItem({
            ...selectedProduct, price: totalPrice, customDescription: attributesDescription,
            selectedAttributes: selectedQuantities,
            attribute_lines: attribute_lines || [],
            nested_attributes: nested_attributes || null,
            id: `${selectedProduct.id}-custom-${Date.now()}`, hasOptions: true
        });
        setSelectedProduct(null);
    };

    if (!sessionData || !sessionData.authenticated) return null;

    return (
        <div className="flex flex-col h-screen overflow-hidden">
            {!selectedProduct && <BrandLogo delay={0.2} topPosition="55px" />}
            <StepIndicator />

            <div className="flex flex-1 overflow-hidden">
            {/* Sidebar: categorías raíz */}
            <div className="w-[130px] flex-shrink-0 shadow-2xl flex flex-col overflow-hidden" style={{ background: 'rgba(255,255,255,0.12)', backdropFilter: 'blur(5px)', WebkitBackdropFilter: 'blur(5px)', borderRight: '1px solid rgba(255,255,255,0.4)' }}>
                <div className="py-6 px-2" style={{ background: 'transparent', borderBottom: '1px solid rgba(0,0,0,0.08)' }}>
                    <div style={{ background: '#fff', borderRadius: '50%', width: 104, height: 104, margin: '0 auto 12px', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 4px 12px rgba(0,0,0,0.18)' }}>
                        <img src={logo} alt="Danés" style={{ width: '92%', height: '92%', objectFit: 'contain' }} />
                    </div>
                    <h2 className="text-lg font-bold text-center" style={{ color: '#009542' }}>Categorías</h2>
                </div>
                <div className="flex-1 overflow-y-auto p-3 space-y-2">
                    {roots.map((category, index) => {
                        const active = activeRoot && activeRoot.id === category.id;
                        return (
                            <button
                                key={category.id}
                                onClick={() => setActiveCategoryId(category.id)}
                                className={`w-full flex items-center px-3 py-8 rounded-xl transition-all duration-300 animate-slide-in shadow-md ${active ? 'shadow-xl' : ''}`}
                                style={{
                                    animationDelay: `${index * 0.05}s`,
                                    backgroundColor: active ? '#ffffff' : '#009542',
                                    color: active ? '#009542' : '#ffffff',
                                    border: active ? '3px solid #009542' : '1px solid rgba(255, 255, 255, 0.35)',
                                    minWidth: 0,
                                }}
                            >
                                <span className="font-bold text-lg flex-1 text-left leading-tight" style={{ wordBreak: 'break-word', overflowWrap: 'break-word' }}>{category.name}</span>
                            </button>
                        );
                    })}
                </div>
            </div>

            {/* Centro: subcategorías o productos */}
            <div className="flex-1 flex flex-col overflow-hidden" style={{ background: 'transparent' }}>
                <div style={{ background: 'rgba(255,255,255,0.55)', padding: '1.8rem 1.5rem', borderBottom: '1px solid #e7e0d3' }}>
                    <div className="max-w-7xl mx-auto flex items-center gap-4">
                        {activeCat && np(activeCat.parent_id) != null && (
                            <button onClick={goUp} className="flex items-center justify-center rounded-xl font-bold"
                                style={{ background: '#007a36', color: '#fff', width: 56, height: 56, fontSize: '1.8rem' }}>
                                ‹
                            </button>
                        )}
                        <h1 className="text-4xl font-bold" style={{ color: '#009542' }}>{activeCat ? activeCat.name : 'Categorías'}</h1>
                        <img src={logoHorizontal} alt="Danés Burgers & Sandwiches" style={{ height: 54, width: 'auto', marginLeft: 'auto' }} />
                    </div>
                </div>

                <div className="flex-1 overflow-y-auto p-4" style={{ background: 'transparent', position: 'relative' }}>
                    {subcats.length > 0 ? (
                        // Subcategorías como tarjetas grandes
                        <div className="grid grid-cols-3 gap-4 p-2">
                            {subcats.map((sc, index) => (
                                <button
                                    key={sc.id}
                                    onClick={() => setActiveCategoryId(sc.id)}
                                    className="animate-fade-in-up rounded-2xl shadow-lg flex flex-col items-center justify-center text-center transition-transform hover:scale-[1.02]"
                                    style={{
                                        animationDelay: `${index * 0.05}s`, minHeight: 170,
                                        background: 'rgba(255,255,255,0.97)', color: '#009542',
                                        border: '1px solid #e5e7eb', borderTop: '5px solid #009542',
                                        boxShadow: '0 8px 22px rgba(0,0,0,0.08)',
                                        cursor: 'pointer', padding: '18px 16px'
                                    }}
                                >
                                    {sc.image && (
                                        <img src={sc.image} alt={sc.name}
                                            style={{ width: 96, height: 96, objectFit: 'contain', marginBottom: 10 }} />
                                    )}
                                    <span className="font-bold" style={{
                                        fontSize: 'clamp(0.9rem, 2vw, 1.3rem)', lineHeight: 1.15, letterSpacing: '0.2px',
                                        hyphens: 'none', overflowWrap: 'break-word'
                                    }}>{sc.name}</span>
                                </button>
                            ))}
                        </div>
                    ) : leafProducts.length === 0 ? (
                        <div className="flex flex-col items-center justify-center h-64 text-center">
                            <h3 className="text-2xl font-medium text-gray-900 mb-2">No hay productos en esta categoría</h3>
                            <p className="text-lg text-gray-600">{isOffline ? 'Conéctate para cargar los productos.' : 'Elige otra categoría.'}</p>
                        </div>
                    ) : (
                        <div className="grid grid-cols-3 gap-3 pb-6">
                            {leafProducts.map((product, index) => (
                                <div key={product.id} className="animate-fade-in-up" style={{ animationDelay: `${Math.min(index, 12) * 0.03}s` }}>
                                    <ProductCard product={product} onAddToCart={handleAddToCart} />
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>

            </div>

            {selectedProduct && (
                <ProductOptionsModal
                    product={selectedProduct}
                    onClose={() => setSelectedProduct(null)}
                    onSubmit={handleModalSubmit}
                />
            )}
        </div>
    );
};

export default ProductListWithSidebar;
